/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import lightFormat from "date-fns/lightFormat";
import type Highcharts from "highcharts/highstock";
import type { SeriesAreaOptions, SeriesBarOptions } from "highcharts/highstock";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { useRef } from "react";
import colors from "tailwindcss/colors";

type KeyPerformanceIndicatorDataSeries = {
  name: string;
  format: string;
  isCurrency: boolean;
  data: {
    x: number;
    y: number | null;
  }[];
};

interface KpisChartProps {
  plotType: "area" | "column";
  kpis: KeyPerformanceIndicatorDataSeries[];
}

export default function KpisChart({ plotType, kpis }: KpisChartProps) {
  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);

  const series = kpis.map((node, index) => {
    const theme = getColorThemeForIndex(index);

    if (!theme) {
      throw new Error("Theme not found");
    }

    const defaultOptions = {
      showInNavigator: true,
      type: plotType,
      animation: false,
      name: node.name,
      yAxis: index,
      color: theme.color,
      lineColor: theme.lineColor,
      lineWidth: 2,
      data: node.data.sort((a, b) => compareAsc(a.x, b.x)),
      custom: {
        primaryColor: theme.color,
        secondaryColor: theme.lineColor,
      },
      marker: {
        width: 8,
        height: 8,
        lineWidth: 1,
        fillColor: theme.color,
        color: theme.color,
        lineColor: theme.lineColor,
        symbol: "circle",
      },
    };

    if (plotType === "column") {
      const columnOptions = {
        borderColor: theme.lineColor,
        borderWidth: 1.5,
        states: {
          hover: {
            color: theme.lineColor,
          },
        },
      };
      return {
        ...columnOptions,
        ...defaultOptions,
      };
    }

    return defaultOptions;
  }) as (SeriesAreaOptions | SeriesBarOptions)[];

  const options: Highcharts.Options = {
    time: {
      timezone: "UTC",
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
    },
    scrollbar: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series,
    legend: {
      align: "left",
      enabled: true,
      padding: 4,
      alignColumns: true,
      itemDistance: 16,
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: colors.slate[800],
        fontFamily: aeonik.style.fontFamily,
        fontWeight: "500",
        fontSize: "12px",
        width: 240,
        textOverflow: "ellipsis",
      },
      reversed: true,
    },
    xAxis: {
      type: "datetime",
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      tickColor: colors.slate[100],
      tickWidth: 1,
      scrollbar: {
        enabled: false,
      },
      labels: {
        step: 2,
        enabled: true,
        formatter: function () {
          // @ts-ignore
          return lightFormat(this.value, "MM/yyyy");
        },
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: kpis.map((node, index) => {
      const minValues = node.data.map((node) => node.y);
      const minValue = minValues.length > 0 ? Math.min(...minValues.filter(notUndefinedOrNull)) : 0;

      return {
        type: "linear",
        min: minValue,
        gridLineColor: colors.slate[100],
        lineWidth: 1,
        lineColor: colors.slate[100],
        labels: {
          enabled: true,
          align: "center",
          style: {
            color: colors.slate[500],
            fontFamily: aeonik.style.fontFamily,
            fontWeight: "500",
            fontSize: "12px",
          },
          formatter: function () {
            const valueFormatter = new Intl.NumberFormat("en", {
              notation: "compact",
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
            });
            return valueFormatter.format(parseFloat(String(this.value)));
          },
        },
        tickWidth: 0,
        showFirstLabel: true,
        showLastLabel: false,
        scrollbar: {
          enabled: false,
        },
        opposite: !!index,
      };
    }),
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 12,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      xDateFormat: "%d/%m/%Y",
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: colors.slate[500],
            color: colors.slate[50],
            lineWidth: 2,
          },
        },
      },
      area: {
        color: colors.teal[50],
        lineColor: colors.teal[600],
        lineWidth: 2,
        dataLabels: {
          enabled: false,
        },
        marker: {
          enabled: false,
          fillColor: colors.teal[50],
          lineColor: colors.teal[600],
          lineWidth: 4,
          width: 8,
          height: 8,
        },
      },
      bar: {
        borderWidth: 1.5,
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };

  return (
    <div className="flex h-full w-full flex-col space-y-4">
      <HighchartsChart ref={chartRef} options={options} className="h-96 xl:h-full" />
    </div>
  );
}
